import { CircularProgress } from '@mui/material'
import { Box } from '@mui/system'
import { useTranslation } from 'react-i18next'

interface Props {
  loading?: boolean
  /** Opcional. Se omitido, usa o border-radius padrão do Card do Dashboard. */
  borderRadius?: string | number
}

/**
 * Overlay translúcido com spinner usado APENAS pelos cards do Dashboard.
 * Foi propositalmente colocado fora do componente compartilhado `Card`
 * porque o `Card` é reutilizado por outras telas que não devem exibir
 * o overlay de loading.
 *
 * Deve ser renderizado como irmão do `<Card>` dentro de um container
 * com `position: relative` — tipicamente o `<ToggableCard>` que já
 * envolve cada card do Dashboard.
 */
export const CardLoadingOverlay = ({ loading, borderRadius = '4px' }: Props) => {
  const { t } = useTranslation()

  if (!loading) return null

  return (
    <Box
      role="status"
      aria-busy="true"
      aria-label={String(t('painel.loading'))}
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'rgba(255, 255, 255, 0.55)',
        backdropFilter: 'blur(1px)',
        borderRadius,
        zIndex: 5,
        // Garante que o overlay não interfira com cliques fora da área visível.
        pointerEvents: 'all',
        // Pequena animação de fade-in/pulse para suavizar a aparição.
        animation: 'cardLoadingOverlayFadeIn 180ms ease-out',
        '@keyframes cardLoadingOverlayFadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <CircularProgress
        size={36}
        thickness={4}
        sx={{ color: '#FF5E1E' }}
      />
    </Box>
  )
}
